import { useEffect, useState } from "react";
import { View, Text, ActivityIndicator, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { apiRequest } from "../app/lib/api";

interface Bar { label: string; count: number }
interface PaginatedResponse { items: Bar[]; page: number; pageSize: number; total: number; totalPages: number }

interface Props {
  title: string;
  endpoint: string;
  barColor?: string;
  /** Set true if endpoint returns {items, page, total, totalPages}. Defaults to false (raw array). */
  paginated?: boolean;
  /** Items per page when paginated. Default 10. */
  pageSize?: number;
}

export function TopNBarChart({ title, endpoint, barColor = "#F5A623", paginated = false, pageSize = 10 }: Props) {
  const [data, setData] = useState<Bar[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    const url = paginated ? `${endpoint}?page=${page}&pageSize=${pageSize}` : endpoint;
    apiRequest<Bar[] | PaginatedResponse>(url)
      .then((res) => {
        if (Array.isArray(res)) {
          setData(res);
          setTotalPages(1);
        } else {
          setData(res.items);
          setTotalPages(res.totalPages);
        }
      })
      .catch(() => {
        setData([]);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, [endpoint, page, paginated, pageSize]);

  const maxCount = Math.max(1, ...data.map((d) => d.count));

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: "#ffffff",
        borderRadius: 14,
        padding: 14,
        borderWidth: 1,
        borderColor: "#e5e7eb",
      }}
    >
      <Text style={{ fontSize: 14, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>{title}</Text>
      {loading ? (
        <View style={{ paddingVertical: 30, alignItems: "center" }}>
          <ActivityIndicator color={barColor} />
        </View>
      ) : data.length === 0 ? (
        <Text style={{ color: "#9ca3af", fontSize: 12, paddingVertical: 20, textAlign: "center" }}>No data yet</Text>
      ) : (
        <>
          <View style={{ gap: 8 }}>
            {data.map((b) => {
              const pct = (b.count / maxCount) * 100;
              return (
                <View key={b.label}>
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 3 }}>
                    <Text
                      style={{ fontSize: 12, color: "#374151", fontWeight: "600", flex: 1, marginRight: 6 }}
                      numberOfLines={1}
                    >
                      {b.label}
                    </Text>
                    <Text style={{ fontSize: 12, color: "#1B2A4A", fontWeight: "700" }}>{b.count}</Text>
                  </View>
                  <View style={{ height: 8, backgroundColor: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
                    <View style={{ height: 8, width: `${pct}%`, backgroundColor: barColor, borderRadius: 4 }} />
                  </View>
                </View>
              );
            })}
          </View>

          {paginated && totalPages > 1 && (
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: "auto", paddingTop: 10, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}>
              <TouchableOpacity
                onPress={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ flexDirection: "row", alignItems: "center", gap: 4, opacity: page === 1 ? 0.3 : 1, paddingHorizontal: 4, paddingVertical: 4 }}
              >
                <Ionicons name="chevron-back" size={14} color="#1B2A4A" />
                <Text style={{ fontSize: 12, color: "#1B2A4A", fontWeight: "600" }}>Prev</Text>
              </TouchableOpacity>
              <Text style={{ fontSize: 11, color: "#6b7280", fontWeight: "600" }}>
                Page {page} of {totalPages}
              </Text>
              <TouchableOpacity
                onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ flexDirection: "row", alignItems: "center", gap: 4, opacity: page === totalPages ? 0.3 : 1, paddingHorizontal: 4, paddingVertical: 4 }}
              >
                <Text style={{ fontSize: 12, color: "#1B2A4A", fontWeight: "600" }}>Next</Text>
                <Ionicons name="chevron-forward" size={14} color="#1B2A4A" />
              </TouchableOpacity>
            </View>
          )}
        </>
      )}
    </View>
  );
}
